import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tag, Edit2, Trash2, Merge, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function InstellingenWebshopCategorieen() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { modus:'hernoem'|'merge'|'verwijder', bron, doel }
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/producten/webshop-categorieen");
      setRows(data || []);
    } catch (e) {
      toast.error("Categorieën laden mislukt");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openHernoem = (naam) => setDialog({ modus: "hernoem", bron: naam, doel: naam });
  const openMerge = (naam) => setDialog({ modus: "merge", bron: naam, doel: "" });
  const openVerwijder = (naam) => setDialog({ modus: "verwijder", bron: naam });

  const bevestig = async () => {
    if (!dialog) return;
    setBusy(true);
    try {
      if (dialog.modus === "verwijder") {
        const { data } = await api.delete(`/producten/webshop-categorieen/${encodeURIComponent(dialog.bron)}`);
        toast.success(`${data.gewijzigd} product(en) uit categorie "${dialog.bron}" gehaald`);
      } else {
        const doel = (dialog.doel || "").trim();
        if (!doel) { toast.error("Doelnaam is verplicht"); setBusy(false); return; }
        const { data } = await api.post("/producten/webshop-categorieen/rename", { van: dialog.bron, naar: doel });
        toast.success(
          dialog.modus === "merge"
            ? `${data.gewijzigd} product(en) samengevoegd in "${doel}"`
            : `${data.gewijzigd} product(en) hernoemd naar "${doel}"`
        );
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Actie mislukt");
    }
    setBusy(false);
  };

  const totaal = rows.reduce((s, r) => s + (r.aantal || 0), 0);

  return (
    <div className="space-y-6 max-w-3xl" data-testid="cat-beheer-page">
      <div>
        <div className="text-sm text-muted-foreground flex items-center gap-1.5">
          <Tag className="h-3.5 w-3.5 text-accent" /> Instellingen
        </div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Webshop-categorieën</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Hernoem typo's, voeg categorieën samen of ruim er één op. Wijzigingen slaan door in alle gekoppelde producten en zijn direct zichtbaar op de webshop.
        </p>
      </div>

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Info className="h-3.5 w-3.5" />
        {loading ? "…" : `${rows.length} categorieën · ${totaal} producten totaal gekoppeld`}
      </div>

      {loading ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin inline" />
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center text-sm text-muted-foreground" data-testid="cat-leeg">
          Nog geen categorieën in gebruik. Ga naar een product → Commerciële data → "Productcategorie webshop" om er een aan te maken.
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl divide-y divide-border">
          {rows.map((r) => (
            <div key={r.naam} className="flex items-center gap-3 p-3" data-testid={`cat-${r.naam}`}>
              <div className="p-1.5 rounded-md bg-secondary shrink-0">
                <Tag className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{r.naam}</div>
                <div className="text-xs text-muted-foreground">
                  <a href={`/producten?webshop_categorie=${encodeURIComponent(r.naam)}`} className="hover:underline">
                    {r.aantal} product{r.aantal === 1 ? "" : "en"}
                  </a>
                </div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <Button size="sm" variant="outline" onClick={() => openHernoem(r.naam)} data-testid={`hernoem-${r.naam}`}>
                  <Edit2 className="h-3.5 w-3.5 mr-1" /> Hernoemen
                </Button>
                <Button size="sm" variant="outline" onClick={() => openMerge(r.naam)} data-testid={`merge-${r.naam}`}>
                  <Merge className="h-3.5 w-3.5 mr-1" /> Samenvoegen
                </Button>
                <Button size="sm" variant="outline" onClick={() => openVerwijder(r.naam)} data-testid={`del-${r.naam}`}
                  className="text-rose-600 hover:bg-rose-50 hover:text-rose-700">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="cat-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialog?.modus === "hernoem" && "Categorie hernoemen"}
              {dialog?.modus === "merge" && "Categorie samenvoegen"}
              {dialog?.modus === "verwijder" && "Categorie verwijderen"}
            </DialogTitle>
            <DialogDescription>
              {dialog?.modus === "hernoem" && <>Hernoem <Badge variant="outline" className="font-mono">{dialog?.bron}</Badge> naar een nieuwe naam. Alle producten worden bijgewerkt.</>}
              {dialog?.modus === "merge" && <>Voeg <Badge variant="outline" className="font-mono">{dialog?.bron}</Badge> samen in een bestaande categorie. Kies uit de lijst hieronder of typ een nieuwe naam.</>}
              {dialog?.modus === "verwijder" && <>Alle producten in <Badge variant="outline" className="font-mono">{dialog?.bron}</Badge> raken hun categorie kwijt (leeg). Doorgaan?</>}
            </DialogDescription>
          </DialogHeader>

          {dialog?.modus !== "verwijder" && (
            <div className="space-y-2">
              <Label className="text-xs">Nieuwe naam</Label>
              <Input
                autoFocus
                list={dialog?.modus === "merge" ? "cat-merge-list" : undefined}
                value={dialog?.doel || ""}
                onChange={(e) => setDialog((d) => ({ ...d, doel: e.target.value }))}
                placeholder={dialog?.modus === "merge" ? "Selecteer of typ bestaande categorie…" : "Nieuwe naam"}
                data-testid="cat-dialog-input"
              />
              {dialog?.modus === "merge" && (
                <datalist id="cat-merge-list">
                  {rows.filter((r) => r.naam !== dialog.bron).map((r) => <option key={r.naam} value={r.naam}>{r.aantal} producten</option>)}
                </datalist>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} disabled={busy}>Annuleren</Button>
            <Button onClick={bevestig} disabled={busy} data-testid="cat-dialog-bevestig"
              className={dialog?.modus === "verwijder" ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}>
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {dialog?.modus === "verwijder" ? "Verwijderen" : "Bevestigen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
